import { type MutateOptions, useMutation } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { gqlClient, queryClient } from '@/lib';
import {
  ExperienceImageTypes,
  type UploadImageForExperienceInput,
} from '@/types/gql.generated';
import type { QueryError } from '@/utils/errors';
import { createExperienceQueryKey } from '@/utils/queryKeys';
import { usePageExperienceContext } from '../contexts/PageExperienceContext';
import type {
  UploadImageForExperienceMutation,
  UploadImageForExperienceMutationVariables,
} from './useUploadImageForExperience.generated';

type MutateFn = (
  file: File | null,
  options?: MutateOptions<
    UploadImageForExperienceMutation,
    QueryError,
    UploadImageForExperienceMutationVariables
  >
) => void;

const query = gql`
  mutation UploadImageForExperience($input: UploadImageForExperienceInput!) {
    uploadImageForExperience(input: $input)
  }
`;

/**
 * Uploads to the loaded experience unless `experienceId` names another one —
 * the planner header's logo belongs to the owning planner rather than the view
 * the author happens to be looking at, so that slot targets the parent.
 */
export const useUploadImageForExperience = (experienceId?: string) => {
  const { experience } = usePageExperienceContext();
  const targetId = experienceId ?? experience.id;

  const { mutate, ...result } = useMutation<
    UploadImageForExperienceMutation,
    QueryError,
    UploadImageForExperienceMutationVariables
  >({
    mutationFn: (variables) => {
      return gqlClient.request<
        UploadImageForExperienceMutation,
        UploadImageForExperienceMutationVariables
      >(query, variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createExperienceQueryKey(targetId),
      });
    },
  });

  const uploadAsset = (
    type: ExperienceImageTypes,
    image: File | null,
    options?: MutateOptions<
      UploadImageForExperienceMutation,
      QueryError,
      UploadImageForExperienceMutationVariables
    >
  ) => {
    const input: UploadImageForExperienceInput = {
      experienceId: targetId,
      type,
      image,
    };
    return mutate({ input }, options);
  };

  const uploadBannerImage: MutateFn = (image, options) =>
    uploadAsset(ExperienceImageTypes.Banner, image, options);

  const uploadLogo: MutateFn = (image, options) =>
    uploadAsset(ExperienceImageTypes.Logo, image, options);

  return {
    ...result,
    uploadAsset,
    uploadBannerImage,
    uploadLogo,
  };
};
